import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Shield, FileText, Video, PenTool, Star, LogIn, Settings } from 'lucide-react';

export default function Home() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="h-14 sm:h-16 border-b bg-background/95 backdrop-blur flex items-center justify-between px-4 sm:px-6">
        <h1 className="text-xl sm:text-2xl font-bold text-primary" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Litige-Protect
        </h1>
        <div className="flex items-center gap-2">
          <button
            data-testid="register-btn"
            onClick={() => navigate('/admin/register')}
            className="flex items-center gap-2 px-4 py-2 border border-accent text-accent text-sm font-medium rounded-md hover:bg-accent/10 transition-all"
          >
            S'inscrire
          </button>
          <button
            data-testid="login-btn"
            onClick={() => navigate('/login')}
            className="flex items-center gap-2 px-4 py-2 bg-accent text-accent-foreground text-sm font-medium rounded-md hover:bg-accent/90 transition-all"
          >
            <LogIn className="w-4 h-4" />
            Se connecter
          </button>
          <button
            data-testid="admin-btn"
            onClick={() => navigate('/admin')}
            className="flex items-center gap-1 px-3 py-2 text-foreground/60 hover:text-foreground text-sm transition-colors"
            title="Espace Admin"
          >
            <Settings className="w-4 h-4" />
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4 sm:p-6">
        <div className="max-w-md w-full space-y-6 sm:space-y-8">
          {/* Hero Section */}
          <div className="text-center space-y-3 sm:space-y-4">
            <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-accent/10 mb-2 sm:mb-4">
              <Shield className="w-8 h-8 sm:w-10 sm:h-10 text-accent" strokeWidth={1.5} />
            </div>
            <h2 className="text-2xl sm:text-4xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Validation d'Installation
            </h2>
            <p className="text-base sm:text-lg text-foreground/80">
              Processus sécurisé de validation en 6 étapes simples
            </p>
          </div>

          {/* Steps Preview */}
          <div className="bg-card border border-border rounded-lg p-4 sm:p-6 space-y-3 sm:space-y-4">
            <h3 className="font-semibold text-base sm:text-lg mb-3 sm:mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Les étapes du processus :
            </h3>
            
            <div className="space-y-2 sm:space-y-3">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-accent/10 flex items-center justify-center">
                  <FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-accent" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-medium text-sm sm:text-base">Informations client</p>
                  <p className="text-xs sm:text-sm text-foreground/60">Vos coordonnées</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-accent/10 flex items-center justify-center">
                  <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-accent" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-medium text-sm sm:text-base">Formulaire de validation</p>
                  <p className="text-xs sm:text-sm text-foreground/60">Confirmations importantes</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-accent/10 flex items-center justify-center">
                  <Video className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-accent" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-medium text-sm sm:text-base">Vidéo témoignage</p>
                  <p className="text-xs sm:text-sm text-foreground/60">Enregistrement (optionnel)</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-accent/10 flex items-center justify-center">
                  <PenTool className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-accent" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-medium text-sm sm:text-base">Signature électronique</p>
                  <p className="text-xs sm:text-sm text-foreground/60">Validation par SMS + GPS</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-accent/10 flex items-center justify-center">
                  <Star className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-accent" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-medium text-sm sm:text-base">Avis client</p>
                  <p className="text-xs sm:text-sm text-foreground/60">Votre retour d'expérience</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-accent/10 flex items-center justify-center">
                  <Shield className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-accent" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-medium text-sm sm:text-base">Confirmation</p>
                  <p className="text-xs sm:text-sm text-foreground/60">Envoi et validation finale</p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <button
            data-testid="start-form-btn"
            onClick={() => navigate('/login')}
            className="w-full h-12 sm:h-14 bg-accent text-accent-foreground text-base sm:text-lg font-medium rounded-md hover:bg-accent/90 border-b-2 border-accent-foreground/20 active:border-b-0 active:translate-y-[2px] transition-all"
          >
            Commencer le processus
          </button>

          {/* Footer Note */}
          <p className="text-center text-xs sm:text-sm text-foreground/60">
            Durée estimée : 5-10 minutes
          </p>
        </div>
      </main>
    </div>
  );
}